import React from 'react';
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, Select } from '@mui/material';

/**
 * Multi-select filter with optional one-click presets.
 * Props: label, value (array of strings), onChange(array), options ([string] or [{value,label}]),
 * presets ([{ label, values: [] }]), size.
 */
export default function MultiSelectFilter({ label, value = [], onChange, options = [], presets = [], size = 'medium', allLabel }) {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const labelOf = (v) => (opts.find((o) => o.value === v) || { label: v }).label;
  const activePreset = presets.find((p) => p.values.length === value.length && p.values.every((v) => value.includes(v)));

  const handleChange = (event) => {
    const next = event.target.value;
    if (typeof next === 'string') { onChange(next ? next.split(',') : []); return; }
    if (next.includes('__all__')) { onChange([]); return; }
    const preset = next.find((v) => typeof v === 'string' && v.startsWith('__preset__'));
    if (preset) { const p = presets[Number(preset.slice('__preset__'.length))]; onChange(p ? [...p.values] : []); return; }
    onChange(next);
  };

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        onChange={handleChange}
        displayEmpty={false}
        renderValue={(selected) => (
          activePreset ? <Chip size="small" label={activePreset.label} />
            : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map((v) => <Chip key={v} size="small" label={labelOf(v)} />)}</Box>
        )}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
      >
        <MenuItem value="__all__" dense>
          <ListItemText primary={allLabel || `All ${label.toLowerCase()}`} primaryTypographyProps={{ fontStyle: value.length ? 'normal' : 'italic' }} />
        </MenuItem>
        {presets.length > 0 && <ListSubheader disableSticky>Quick filters</ListSubheader>}
        {presets.map((p, i) => (
          <MenuItem key={p.label} value={`__preset__${i}`} dense selected={activePreset === p}>
            <ListItemText primary={p.label} secondary={p.values.map(labelOf).join(', ')} secondaryTypographyProps={{ variant: 'caption' }} />
          </MenuItem>
        ))}
        {presets.length > 0 && <ListSubheader disableSticky>Individual</ListSubheader>}
        {opts.map((o) => (
          <MenuItem key={o.value} value={o.value} dense>
            <Checkbox size="small" checked={value.includes(o.value)} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const DETECTION_STATUS_PRESETS = [
  { label: 'Open (not closed)', values: ['New', 'In Progress', 'Under Review'] },
  { label: 'Needs review', values: ['New', 'Under Review'] },
  { label: 'Resolved or closed', values: ['Resolved', 'False Positive', 'Closed'] },
];
export const CASE_STATUS_PRESETS = [
  { label: 'Open (not closed)', values: ['Open', 'In Progress', 'Under Review'] },
  { label: 'Resolved or closed', values: ['Resolved', 'Closed'] },
];
export const SEVERITY_PRESETS = [
  { label: 'High and above', values: ['High', 'Critical'] },
  { label: 'Medium and above', values: ['Medium', 'High', 'Critical'] },
];
